/**
 * SampleAndHoldModule — a stepped random modulation source.
 *
 * A setTimeout-scheduled clock picks a fresh random value in [-1, +1] each
 * period (period = 1 / rate). The output is a ConstantSourceNode whose
 * `offset` is driven toward the new value:
 *
 *   slew = 0   → `setValueAtTime` (classic hard-edged S&H)
 *   slew > 0   → `linearRampToValueAtTime(target, now + slew × period)`
 *
 * So slew=1 produces continuous random drift (more like a chaotic LFO); slew
 * near 0 is the angry square-stepped S&H every analog patch loves. Control of
 * the clock via JS setTimeout has sub-millisecond jitter — plenty tight for
 * slow musical rates. When we want sample-accurate rhythmic S&H (1/16 notes
 * etc.), this will move into an AudioWorklet, but that's Phase 5 work.
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export class SampleAndHoldModule implements SynthModule {
  readonly kind = 'modulation' as const;
  readonly type = 'sh' as const;
  readonly displayName = 'S&H';
  readonly id: string;
  bypass = false;

  readonly rate = new NumericParameter({
    name: 'Rate',
    default: 4,
    min: 0.05,
    max: 50,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 0,
    formatter: (v) => (v < 1 ? v.toFixed(2) : v.toFixed(1)),
  });
  readonly slew = new NumericParameter({
    name: 'Slew',
    default: 0,
    min: 0,
    max: 1,
    curve: 'linear',
    smoothingMs: 0,
    formatter: (v) => `${Math.round(v * 100)}%`,
  });

  readonly parameters: readonly AnyParameter[] = [this.rate, this.slew];

  readonly input: AudioNode | null = null;
  readonly output: AudioNode | null = null;

  private source: ConstantSourceNode | null = null;
  private engine: AudioEngine | null = null;
  private tickTimer: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(id?: string) {
    this.id = id ?? mintId('sh');
  }

  get modOutput(): AudioNode | null {
    return this.source;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    this.engine = engine;
    const ctx = engine.context;

    this.source = ctx.createConstantSource();
    this.source.offset.value = 0;
    this.source.start();
    this.started = true;

    // When Rate changes, the setTimeout already queued up is still running on
    // the old period — at slow rates that can leave the user staring at the
    // knob for seconds. Reschedule on every rate change so the new rate
    // takes effect immediately (a fresh random value + a timer sized to the
    // new period).
    this.unsubs.push(this.rate.subscribe(() => this.scheduleNextTick()));

    this.scheduleNextTick();
  }

  /**
   * Advance to a new random value and schedule the next tick. Exposed for
   * tests — production code calls it once from init() and the timer takes
   * over from there.
   */
  scheduleNextTick(): void {
    if (!this.engine || !this.source) return;
    const ctx = this.engine.context;
    const period = 1 / Math.max(0.05, this.rate.value);
    const slewTime = Math.max(0, Math.min(period, this.slew.value * period));
    const value = Math.random() * 2 - 1; // [-1, +1]

    const offset = this.source.offset;
    const t = ctx.currentTime;

    if (slewTime <= 0.001) {
      // Hard step. Cancel any pending schedule, then a single setValueAtTime.
      // CRITICAL: do NOT anchor with setValueAtTime(current, t) here — the
      // Web Audio runtime silently ignores a second setValueAtTime scheduled
      // at the exact same time as a previous one, which would swallow the
      // real value write.
      offset.cancelScheduledValues(t);
      offset.setValueAtTime(value, t);
    } else {
      // Linear ramp. Anchor at t, ramp to t + slewTime — different times,
      // so no same-time collision.
      const current = offset.value;
      offset.cancelScheduledValues(t);
      offset.setValueAtTime(current, t);
      offset.linearRampToValueAtTime(value, t + slewTime);
    }

    if (this.tickTimer) clearTimeout(this.tickTimer);
    this.tickTimer = setTimeout(() => this.scheduleNextTick(), period * 1000);
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    if (this.tickTimer) {
      clearTimeout(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        /* already stopped */
      }
      this.source.disconnect();
      this.source = null;
    }
    this.started = false;
    this.engine = null;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }
}
